import React, { useCallback } from "react";
import { AuthContext, RedirectRequest } from "../AuthContext";
import { logger, setAuthenticatedUserContext } from "../../shared/services/loggingService";
import { AuthenticationResult, AuthError, BrowserCacheLocation, CacheLookupPolicy, Configuration, EventType, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { LogLevel } from "@azure/msal-browser";
import { useSingleton } from "../../shared/hooks";
import { classifyAuthError, clearPasswordResetInProgress, isPasswordResetInProgress, isPasswordResetResult, setPasswordResetInProgress } from "./authHelpers";

type ExternalTokenData = {
    access_token: string;
    id_token: string;
    token_type?: string;
    expires_in?: number;
};

/// Attempt to acquire an external token from local storage and parse it into an `AuthenticationResult` object.
///
/// This is used during E2E testing to allow the use of PAT-based authentication acquired externally.
const acquireExternalAuthResultFromLocalStorage = (): AuthenticationResult | null => {
    const _externalTokens = localStorage.getItem("auth.external-tokens");
    if (!_externalTokens) return null;

    // Decode the base64-encoded payload of the JWT...
    const parseToken = (token: string): Record<string, any> => {
        return JSON.parse(atob(token.split('.')[1]));
    } 

    const externalTokens: ExternalTokenData = JSON.parse(_externalTokens);
    const accessToken = parseToken(externalTokens.access_token);
    const idToken = parseToken(externalTokens.id_token);

    // Short-circuit if the external token has expired...
    if (accessToken.exp < Date.now() / 1000) return null;

    return {
        authority: accessToken.iss,
        uniqueId: accessToken.sub,
        tenantId: accessToken.tid,
        scopes: [accessToken.aud],
        account: {
            homeAccountId: accessToken.sub,
            environment: accessToken.iss,
            tenantId: accessToken.iss,
            username: accessToken.email,
            localAccountId: accessToken.sub,
            name: accessToken.name,
            idToken: externalTokens.id_token,
            idTokenClaims: idToken
        },
        idToken: externalTokens.id_token,
        idTokenClaims: idToken,
        accessToken: externalTokens.access_token,
        fromCache: true,
        expiresOn: new Date(accessToken.exp * 1000),
        tokenType: "JWT",
    } as AuthenticationResult;
}

export type B2CAuthProviderProps = {
    children: any;
    tenantId: string;
    instance: string;
    clientId: string;
    domain: string;
    redirectUri: string;
    apiVersion: string;
    environment: string;
    appDomain: string;
    onRedirectCallback: (state: object) => void;
}

export const B2CAuthProvider = (props: B2CAuthProviderProps) => {
    const { children, instance, clientId, domain, redirectUri, apiVersion, environment, onRedirectCallback, appDomain } = props;

    const b2cPolicies = {
        names: {
            signUpSignIn: 'B2C_1A_SIGNUP_SIGNIN',
            forgotPassword: 'BSC_1A_PASSWORDRESET',
            editProfile: 'B2C_1A_PROFILEEDIT',
        },
        authorities: {
            signUpSignIn: {
                authority: `${instance}${domain}/B2C_1A_SIGNUP_SIGNIN`,
            },
            forgotPassword: {
                authority: `${instance}${domain}/B2C_1A_PASSWORDRESET`,
            },
            editProfile: {
                authority: `${instance}${domain}/B2C_1A_PROFILEEDIT`,
            },
        },
        authorityDomain: instance.replace("https://", "").replace("/", ""),
    };

    const loginRequest: RedirectRequest = {
        scopes: ['openid', 'offline_access', clientId],
        extraQueryParameters: {
            "domain": appDomain,
            "api-version": apiVersion,
            "env": environment
        },
        // Default redirect to current location - this will be overridden by private route redirects, if needed...
        targetUrl: window.location.href
    };

    const msalConfig: Configuration = {
        auth: {
            clientId: clientId, // This is the ONLY mandatory field that you need to supply.
            authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
            knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
            redirectUri: redirectUri, // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
            postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
            navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
            // Explicitly provide the authority metadata to prevent an unnecessary request to the authority metadata endpoint on each page load.
            // This will only change if we make explicit changes to the B2C tenant's configuration, so it's safe to "cache" this value.
            // CAVEAT: MSAL applies this single metadata blob to EVERY authority used by the client (including per-request
            // authority overrides), so it MUST be suppressed whenever the password-reset policy is in play - otherwise
            // password-reset requests resolve to the sign-in policy's endpoints.  That is the case on the AADB2C90118
            // return leg ("Forgot your password?" clicked), and for the remainder of the reset flow (which spans several
            // full page loads - tracked via the password-reset-in-progress flag)...
            authorityMetadata: window.location.href.includes("AADB2C90118") || isPasswordResetInProgress() ? undefined : `{
    "issuer": "https://qerent.b2clogin.com/bed0f7cf-d61b-4bd8-a794-22e2a77e83ca/v2.0/",
    "authorization_endpoint": "https://qerent.b2clogin.com/qerent.onmicrosoft.com/b2c_1a_signup_signin/oauth2/v2.0/authorize",
    "token_endpoint": "https://qerent.b2clogin.com/qerent.onmicrosoft.com/b2c_1a_signup_signin/oauth2/v2.0/token",
    "end_session_endpoint": "https://qerent.b2clogin.com/qerent.onmicrosoft.com/b2c_1a_signup_signin/oauth2/v2.0/logout",
    "jwks_uri": "https://qerent.b2clogin.com/qerent.onmicrosoft.com/b2c_1a_signup_signin/discovery/v2.0/keys",
    "response_modes_supported": [
        "query",
        "fragment",
        "form_post"
    ],
    "response_types_supported": [
        "code",
        "code id_token",
        "code token",
        "code id_token token",
        "id_token",
        "id_token token",
        "token",
        "token id_token"
    ],
    "scopes_supported": [
        "openid"
    ],
    "subject_types_supported": [
        "pairwise"
    ],
    "id_token_signing_alg_values_supported": [
        "RS256"
    ],
    "token_endpoint_auth_methods_supported": [
        "client_secret_post",
        "client_secret_basic"
    ],
    "claims_supported": [
        "name",
        "given_name",
        "family_name",
        "email",
        "sub",
        "tid",
        "user_id",
        "domain",
        "tenant_name",
        "roles",
        "allowed_tags",
        "denied_tags",
        "user_metadata",
        "iss",
        "iat",
        "exp",
        "aud",
        "acr",
        "nonce",
        "auth_time"
    ]
}`,
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
            temporaryCacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
        },
        system: {
            loggerOptions: {
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case LogLevel.Error:
                            console.error(message);
                            return;
                        case LogLevel.Warning:
                            console.warn(message);
                            return;
                        default:
                            return;
                    }
                },
            },
        },
    };

    const [client, setClient] =  React.useState<PublicClientApplication | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const externalAuthResult = useSingleton(() => acquireExternalAuthResultFromLocalStorage());
    // Tracks an in-flight interactive (popup/redirect) token acquisition.  `getTokenSilently`
    // runs on every outgoing request, so without coalescing these, multiple concurrent calls
    // would each open their own popup and fail with an "interaction_in_progress" error.
    const interactiveTokenRequest = React.useRef<Promise<AuthenticationResult | null> | null>(null);

    const getFullAuthRequest = useCallback((originalRequest?: Record<string, any>) => {
        // Transform the target URL into a state value...
        const { targetUrl: _redirectUrl, ...rest } = { ...loginRequest, ...originalRequest };
        const redirectUrl = new URL(_redirectUrl ?? window.location.href);
        const targetUrl = 
            // If the current path contains a redirect parameter, use that as-is...
            redirectUrl?.searchParams?.get("original-path") ??
             // Otherwise, ensure that the URL does not contain a hash fragment...
            (redirectUrl.origin + redirectUrl.pathname + redirectUrl.search);

        return { ...rest, state: JSON.stringify({ targetUrl }) };
    }, []);

    // Sends the user into the B2C password-reset policy.  Uses a dedicated client with the hard-coded
    // sign-in `authorityMetadata` stripped, so the password-reset authority's endpoints are discovered
    // properly (the main client may already have the sign-in metadata applied to all authorities).  The
    // temporary request cache is shared (same clientId + localStorage), so the return leg is picked up
    // by the main client as usual - with the in-progress flag suppressing the hard-coded metadata on
    // those page loads until the flow concludes...
    const redirectToPasswordReset = async () => {
        setPasswordResetInProgress();
        const resetClient = new PublicClientApplication({ ...msalConfig, auth: { ...msalConfig.auth, authorityMetadata: undefined } });
        await resetClient.initialize();
        await resetClient.loginRedirect(getFullAuthRequest({
            ...loginRequest,
            authority: b2cPolicies.authorities.forgotPassword.authority
        }));
    };

    React.useEffect(() => {
        if (!client) {
            const _client = new PublicClientApplication(msalConfig);
            _client.initialize().then(() => setClient(_client));
            return;
        }

        client.handleRedirectPromise()
            .then(_response => {
                clearPasswordResetInProgress();

                // The user has just completed the password-reset flow.  The token issued by the reset
                // policy carries none of the application claims (roles, tags, metadata), so it must not
                // become the session - discard it and re-run the sign-in policy instead (the user signs
                // in with their new password).  `loading` stays true so nothing renders while the
                // browser navigates...
                if (isPasswordResetResult(_response)) {
                    let targetUrl: string | undefined;
                    try {
                        targetUrl = JSON.parse(_response.state ?? "{}")?.targetUrl;
                    }
                    catch { /* Malformed state - fall back to the current location... */ }

                    client.loginRedirect(getFullAuthRequest({ targetUrl }));
                    return;
                }

                setLoading(false);
            })
            .catch((err: AuthError) => {
                // The user clicked "Forgot your password?" on the sign-in page - B2C reports this as an
                // error (AADB2C90118) on the return leg.  Kick off the password-reset flow instead of
                // surfacing it; `loading` stays true so nothing renders while the browser navigates...
                if (classifyAuthError(err) === "forgot-password") {
                    redirectToPasswordReset();
                    return;
                }

                // The user cancelled out of the password-reset flow (AADB2C90091) - send them straight
                // back to the sign-in page (default authority) rather than stranding them on the error
                // card.  `getFullAuthRequest` preserves any original-path, so a subsequent successful
                // sign-in still returns them to the page they came from...
                if (classifyAuthError(err) === "user-cancelled") {
                    clearPasswordResetInProgress();
                    client.loginRedirect(getFullAuthRequest());
                    return;
                }

                clearPasswordResetInProgress();
                setLoading(false);
                const sanitizedErrorMessage = err.errorMessage?.split?.("\n")?.[0]?.replace?.("AADB2C: ", "");
                setError(sanitizedErrorMessage);

                const sanitizedPath =
                    new URLSearchParams(window.location.search)?.get("original-path") ?? // If the current path already contains a redirect parameter, use that as-is...
                    window.location.origin + window.location.pathname + window.location.search; // Otherwise, use the current path with the hash fragment removed...
                onRedirectCallback && onRedirectCallback({ targetUrl: `/login-callback?original-path=${sanitizedPath}` });
            });

        const callbackId = client.addEventCallback((event) => {
            // If the user has successfully logged in, then we need to set the active account and track the login event...
            // Tokens issued by the password-reset policy are excluded - they lack the application claims
            // and are discarded in favor of a fresh sign-in (see the `handleRedirectPromise` handler)...
            if (
                (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
                (event.payload as AuthenticationResult).account &&
                !isPasswordResetResult(event.payload as AuthenticationResult)
            ) {
                const authResult = event.payload as AuthenticationResult;
                const account = authResult.account;
                client.setActiveAccount(account);
                logger.trackEvent({ name: "Login" }, account);
            }
        });

        return () => {
            if (callbackId) {
                client.removeEventCallback(callbackId);
            }
        };
    }, [client]);

    const activeUser = externalAuthResult?.account ?? client?.getActiveAccount();
    if (activeUser != null) {
        setAuthenticatedUserContext(activeUser.idTokenClaims?.["email"] as string);
    }

    const roles = activeUser?.idTokenClaims?.["roles"] as string[] ?? [];
    const allowedTags = activeUser?.idTokenClaims?.["allowed_tags"] as string[] ?? [];
    const deniedTags = activeUser?.idTokenClaims?.["denied_tags"] as string[] ?? [];
    const effectiveTags = allowedTags.filter(t => !deniedTags.includes(t));

    return (
        <AuthContext.Provider
            value={{
                onRedirectCallback,
                isAuthenticated: activeUser !== null,
                user: { name: activeUser?.name },
                roles: roles,
                allowedTags: allowedTags,
                deniedTags: deniedTags,
                effectiveTags: effectiveTags,
                userMetadata: Object.fromEntries(
                    [
                        // Non-standard claim names, as returned originally by the "getUserMetadata" function call...
                        ["EmailAddress", activeUser?.idTokenClaims?.["email"] ?? ""],
                        ["UserId", activeUser?.idTokenClaims?.["user_id"] ?? ""],
                        // Standardized claims...
                        ["domain", activeUser?.idTokenClaims?.["domain"] ?? ""],
                        ["tenant_name", activeUser?.idTokenClaims?.["tenant_name"] ?? ""],
                        ["roles", roles],
                        ["user_id", activeUser?.idTokenClaims?.["user_id"] ?? ""],
                        ["name", activeUser?.idTokenClaims?.["name"] ?? ""],
                        ["email", activeUser?.idTokenClaims?.["email"] ?? ""],
                        ["allowed_tags", allowedTags],
                        ["denied_tags", deniedTags],
                        ["effective_tags", effectiveTags],
                        // User metadata...
                        ...((activeUser?.idTokenClaims?.["user_metadata"] ?? []) as string[]).map(s => s.split("::")),
                    ]
                ),
                loading: client === null,
                error: error,
                loginWithPopup: (req) => client.loginPopup(getFullAuthRequest(req)),
                handleRedirectPromise: (p) => client.handleRedirectPromise(p),
                getIdTokenClaims: () => client.getActiveAccount()?.idTokenClaims,
                loginWithRedirect: (req: RedirectRequest) => client.loginRedirect(getFullAuthRequest(req)),
                getTokenSilently: async () => {
                    if (externalAuthResult)
                        return externalAuthResult.accessToken;

                    const req = getFullAuthRequest();
                    const account = client.getActiveAccount() ?? client.getAllAccounts()[0];

                    // Try silent auth first - returns the cached access token, or renews it via the
                    // stored refresh token when it has expired.  `AccessTokenAndRefreshToken` skips
                    // the hidden-iframe renewal that B2C cannot satisfy when third-party cookies are
                    // blocked (it fails with "AADB2C90077: User does not have an existing session").
                    // Instead this fails fast with an InteractionRequiredAuthError once the refresh
                    // token has also expired.  Silent auth requires an `account`; without one MSAL
                    // cannot perform a silent request at all.
                    if (account) {
                        try {
                            const result = await client.acquireTokenSilent({
                                ...req,
                                account,
                                cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
                            });
                            return result.accessToken;
                        } catch (error) {
                            // Only an "interaction required" error is recoverable via a popup/redirect.
                            // Anything else (e.g. a transient network failure) should propagate.
                            if (!(error instanceof InteractionRequiredAuthError)) {
                                console.error("Silent token acquisition failed:", error);
                                throw error;
                            }
                        }
                    }

                    // Interaction is required.  Because this runs on every outgoing request, many
                    // calls can reach this point at once; coalesce them into a single attempt so the
                    // concurrent calls don't collide with an "interaction_in_progress" error.
                    if (!interactiveTokenRequest.current) {
                        // Attempt to retrieve a new access token via a pop-up window.  It this fails
                        // (pop-up windows may be blocked by the client's browser), fall back to
                        // a full redirect instead.
                        interactiveTokenRequest.current = client.acquireTokenPopup({ ...req, account })
                            .catch(async (popupError) => {
                                // The user clicked "Forgot your password?" inside the popup (AADB2C90118) -
                                // continue into the password-reset flow via a full redirect...
                                if (classifyAuthError(popupError) === "forgot-password") {
                                    await redirectToPasswordReset();
                                    return null;
                                }

                                console.error("Interactive popup failed; falling back to a redirect:", popupError);
                                await client.loginRedirect(req);
                                return null;
                            })
                            .finally(() => { interactiveTokenRequest.current = null; });
                    }

                    const result = await interactiveTokenRequest.current;
                    return result?.accessToken;
                },
                getTokenWithPopup: (req) => client.acquireTokenPopup(getFullAuthRequest(req)),
                logout: (req) => {
                    // Sanitize the postLogoutRedirectUri to ensure it is a valid URL...
                    const { postLogoutRedirectUri: _redirectUrl, ...rest } = req;
                    const redirectUrl = new URL(_redirectUrl ?? window.location.href);
                    const targetUrl = redirectUrl.origin + redirectUrl.pathname + redirectUrl.search;

                    return client.logoutRedirect({ ...rest, postLogoutRedirectUri: targetUrl });
                }
            }}
        >
            {!loading && children}
        </AuthContext.Provider>
    );
};